import {
  ApiError,
  apiUrl,
  clearAuthTokens,
  getRefreshToken,
  read,
  setAuthToken,
  setSessionMode,
  write,
} from './http';
import { DEMO_OTP_CODE, MOCK_PROFILE } from './mock/manager.mock';
import type { ManagerProfile } from '@/types';

/**
 * Player authentication, as the API implements it.
 *
 * The API is a BFF in front of a public Cognito app client: it owns the pool
 * credentials, the PKCE exchange and the SMS challenge, and this app never
 * speaks to Cognito directly. There are two ways in.
 *
 *   Phone   `/auth/mobile/otp` then `/auth/mobile/verify`. The API answers with
 *           Cognito's tokens, which are carried as a bearer from then on.
 *
 *   Google  A full-page trip to `/auth/google`. The API keeps the tokens and
 *           sets them as HTTP-only cookies on its own origin, so the app never
 *           holds them — it only knows the session exists.
 *
 * There is no password login. `/auth/session` still exists on the API but is
 * labelled there as scaffolding for `AUTH_DISABLED`, not a production login,
 * so nothing here calls it.
 */

/* ------------------------------------------------------------------ *
 * The API's shapes
 * ------------------------------------------------------------------ */

/**
 * Which code the API is waiting for.
 *
 * `SIGN_UP` means Cognito has just created the account and sent a confirmation
 * SMS; `SMS_OTP` is an ordinary sign-in code. They are answered by different
 * Cognito calls, so the value has to be handed back on verify.
 */
export type MobileChallenge = 'SMS_OTP' | 'SIGN_UP';

/** The API's `ManagerResponseDto`. */
interface ApiManager {
  id: string;
  email?: string;
  displayName: string;
  teamName?: string;
  phone?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** The API's `CognitoAuthTokensDto` — one shape for both outcomes. */
interface ApiAuthResult {
  status: 'authenticated' | 'challenge';
  challenge?: MobileChallenge;
  session?: string;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  user?: ApiManager;
}

/* ------------------------------------------------------------------ *
 * Our shapes
 * ------------------------------------------------------------------ */

export interface OtpChallenge {
  challenge: MobileChallenge;
  /** Cognito's opaque challenge session. Must be handed back unchanged. */
  session?: string;
}

export type VerifyResult =
  | ({ status: 'challenge' } & OtpChallenge)
  | { status: 'authenticated'; manager: ManagerProfile };

/* ------------------------------------------------------------------ *
 * Mapping
 * ------------------------------------------------------------------ */

/**
 * Normalise a typed phone number to E.164.
 *
 * The API applies the same transform before validating, so doing it here only
 * means a mistyped number is reported by the field rather than by the server.
 *
 * Players type their number the way it is written locally, so the Kenyan forms
 * are expanded to +254: `0712 345 678` and `0112 345 678` (trunk prefix), a
 * bare `712345678`, and `254712345678` without its plus. Anything already
 * starting with `+` is taken as international and left alone.
 */
export function toE164(input: string): string {
  const trimmed = input.trim().replace(/[\s().-]/g, '');
  if (trimmed.startsWith('+')) return trimmed;

  if (/^0[17]\d{8}$/.test(trimmed)) return `+254${trimmed.slice(1)}`;
  if (/^[17]\d{8}$/.test(trimmed)) return `+254${trimmed}`;

  // Includes `254…` typed without the plus.
  return `+${trimmed}`;
}

/** Loose enough to catch a mistyped entry, not to validate a country. */
export const isE164 = (phone: string): boolean => /^\+[1-9]\d{7,14}$/.test(phone);

const formatJoined = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

/**
 * The API's manager record, as the app models it.
 *
 * A phone sign-up has no email — Cognito was given a number, not an address —
 * so the number stands in wherever the account is identified.
 *
 * A manager who has not named a team yet gets an empty `teamName` rather than
 * a placeholder, so screens can word the gap themselves and a team entered
 * without a name is not silently called "Your team".
 */
function toManagerProfile(api: ApiManager): ManagerProfile {
  return {
    id: api.id,
    name: api.displayName,
    email: api.email ?? api.phone ?? '',
    teamName: api.teamName ?? '',
    joined: formatJoined(api.createdAt),
  };
}

/** The manager the demo build signs in as when no API is configured. */
const demoManager = (): ApiManager => ({
  id: MOCK_PROFILE.id,
  email: MOCK_PROFILE.email,
  displayName: MOCK_PROFILE.name,
  teamName: MOCK_PROFILE.teamName,
  status: 'active',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

/* ------------------------------------------------------------------ *
 * Phone
 * ------------------------------------------------------------------ */

/**
 * POST /api/v1/auth/mobile/otp
 *
 * Starts the SMS challenge. The API signs the number up with Cognito if it has
 * never been seen, so there is no separate registration step — which challenge
 * comes back is the only difference between a new player and a returning one.
 */
export function requestMobileOtp(phone: string): Promise<OtpChallenge> {
  return write<OtpChallenge>(
    '/auth/mobile/otp',
    'POST',
    { phone: toE164(phone) },
    // With no API there is no SMS either. The demo accepts a fixed code, the
    // same one the API itself accepts when AUTH_DISABLED is set.
    () => ({ challenge: 'SMS_OTP', session: 'demo-session' }),
  );
}

/**
 * POST /api/v1/auth/mobile/verify
 *
 * A `SIGN_UP` confirmation can be answered with a second challenge: Cognito
 * confirms the account, then asks for a sign-in code and sends another SMS.
 * The caller has to be prepared to collect a code twice.
 */
export async function verifyMobileOtp(input: {
  phone: string;
  code: string;
  challenge: MobileChallenge;
  session?: string;
}): Promise<VerifyResult> {
  const body = {
    phone: toE164(input.phone),
    code: input.code.trim(),
    challenge: input.challenge,
    ...(input.session ? { session: input.session } : {}),
  };

  const result = await write<ApiAuthResult>('/auth/mobile/verify', 'POST', body, () => {
    if (body.code !== DEMO_OTP_CODE) {
      throw new ApiError('That code was not recognised.', 401);
    }
    return { status: 'authenticated', accessToken: 'demo-token', user: demoManager() };
  });

  if (result.status === 'challenge') {
    return {
      status: 'challenge',
      challenge: result.challenge ?? 'SMS_OTP',
      ...(result.session ? { session: result.session } : {}),
    };
  }

  if (!result.accessToken || !result.user) {
    throw new ApiError('Signed in, but the session came back empty.', 502);
  }

  setSessionMode('bearer');
  setAuthToken(result.accessToken, result.refreshToken ?? null);

  return { status: 'authenticated', manager: toManagerProfile(result.user) };
}

/* ------------------------------------------------------------------ *
 * Google
 * ------------------------------------------------------------------ */

/**
 * Where to send the browser to start Google sign-in.
 *
 * This is a navigation, not a request: the API answers with a 302 to Cognito,
 * and the PKCE verifier it needs on the way back rides a cookie set on that
 * same response. Fetching the URL would strand both.
 *
 * `returnTo` must appear in the API's `AUTH_REDIRECT_ALLOWLIST`, or the API
 * refuses to start the flow.
 */
export function googleSignInUrl(returnTo: string): string {
  return `${apiUrl('/auth/google')}?redirectTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Take up the session the Google callback just established.
 *
 * The tokens are in HTTP-only cookies on the API's origin, so there is nothing
 * to read locally and nothing to store: the only way to know the sign-in
 * worked is to make a credentialed request and see who answers.
 */
export async function adoptGoogleSession(): Promise<ManagerProfile> {
  setSessionMode('cookie');

  try {
    return await getCurrentManager();
  } catch (cause) {
    // A cookie that does not authenticate is worse than none — it would leave
    // every later request asking for credentials the API has already refused.
    clearAuthTokens();
    throw cause;
  }
}

/* ------------------------------------------------------------------ *
 * Session
 * ------------------------------------------------------------------ */

/**
 * GET /api/v1/auth/me
 *
 * Resolves whichever session is in play — bearer or cookie — to the manager
 * behind it. Also how a restored session is checked on boot: a token in
 * storage means nothing until the API agrees.
 */
export async function getCurrentManager(signal?: AbortSignal): Promise<ManagerProfile> {
  const api = await read<ApiManager>('/auth/me', demoManager, signal);
  return toManagerProfile(api);
}

/**
 * The fields a manager may change about themselves.
 *
 * `teamName` is optional because the API cannot clear it — it rejects anything
 * shorter than two characters — so a blank one is left out rather than sent.
 */
export interface ProfileUpdate {
  displayName: string;
  teamName?: string;
}

/** The API's `UpdateManagerDto` requires at least this many characters per name. */
export const PROFILE_NAME_MIN_LENGTH = 2;

/**
 * PATCH /api/v1/auth/me
 *
 * Answers with the updated manager, so the caller can adopt it directly rather
 * than asking `/auth/me` again.
 */
export async function updateProfile(
  current: ManagerProfile,
  update: ProfileUpdate,
): Promise<ManagerProfile> {
  const api = await write<ApiManager>('/auth/me', 'PATCH', update, () => ({
    ...demoManager(),
    id: current.id,
    email: current.email,
    displayName: update.displayName,
    teamName: update.teamName ?? current.teamName,
  }));

  return toManagerProfile(api);
}

/**
 * POST /api/v1/auth/refresh
 *
 * Registered with the transport as the player app's refresher, so an expired
 * access token is renewed mid-request rather than ending the session.
 *
 * Only bearer sessions can refresh. A Google session's refresh token is in an
 * HTTP-only cookie and this endpoint reads the token from the body, so there
 * is nothing the app is able to send.
 */
export async function refreshPlayerSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (refreshToken === null) return false;

  try {
    const result = await write<ApiAuthResult>(
      '/auth/refresh',
      'POST',
      { refreshToken },
      () => {
        throw new ApiError('Could not reach the server.', 0);
      },
    );

    if (!result.accessToken) return false;

    setAuthToken(result.accessToken, result.refreshToken ?? refreshToken);
    return true;
  } catch {
    return false;
  }
}

/**
 * POST /api/v1/auth/logout
 *
 * Revokes the refresh token at Cognito, then drops the local session. Best
 * effort: a failed revocation must not leave someone signed in on the device
 * in front of them.
 */
export async function signOut(): Promise<void> {
  const refreshToken = getRefreshToken();

  try {
    if (refreshToken !== null) {
      await write<void>('/auth/logout', 'POST', { refreshToken }, () => undefined);
    }
  } catch {
    // Nothing to do about it, and nothing worth saying to the player.
  } finally {
    clearAuthTokens();
  }
}
